import React, { useEffect } from "react";

/**
 * ConfirmDialog — reusable modal confirmation popup.
 *
 * Props:
 *   isOpen      {boolean}   — controls visibility
 *   title       {string}    — bold heading line (e.g. "12 May 2026")
 *   subtitle    {string}    — secondary line (e.g. "09:30 AM (Morning)")
 *   message     {string}    — body question text
 *   onConfirm   {function}  — called when "Yes" is clicked
 *   onCancel    {function}  — called when "No" or ✕ is clicked
 *   isLoading   {boolean}   — shows spinner + disables buttons during async op
 *   confirmLabel {string}   — defaults to "Yes"
 *   cancelLabel  {string}   — defaults to "No"
 */
const ConfirmDialog = ({
  isOpen,
  title,
  subtitle,
  message = "Are you sure you want to proceed?",
  onConfirm,
  onCancel,
  isLoading = false,
  confirmLabel = "Yes",
  cancelLabel = "No",
}) => {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const BORDER_RADIUS = "4px";

  return (
    <>
      {/* ── Backdrop ── */}
      <div
        onClick={() => !isLoading && onCancel?.()}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 1100,
        }}
      />

      {/* ── Dialog panel ── */}
      <div
        role="dialog"
        aria-modal="true"
        className="w-[300px] md:w-[500px]"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1101,
          background: "#fff",
          borderRadius: "8px",
          // width: "500px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          overflow: "hidden",
          border: "1px solid #CCCCCC",
        }}
      >
        {/* ── Header bar ── */}
        <div
          style={{
            background: "#E7E9EB",
            color: "#111729",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: "1px solid #CCCCCC",
          }}
        >
          <span
            style={{
              fontSize: "18px",
              fontWeight: 600,
              letterSpacing: "0.02em",
            }}
          >
            Confirming...
          </span>
          <button
            type="button"
            onClick={() => !isLoading && onCancel?.()}
            disabled={isLoading}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              color: "#000000",
              cursor: isLoading ? "not-allowed" : "pointer",
              padding: "2px 4px",
              lineHeight: 1,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M1 1l10 10M11 1L1 11"
                stroke="#000000"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "20px 20px 16px" }}>
          {/* Date / time headline */}
          {(title || subtitle) && (
            <div style={{ marginBottom: "12px", textAlign: "center" }}>
              {title && (
                <p
                  style={{
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "#111729",
                    margin: 0,
                  }}
                >
                  {title}
                </p>
              )}
              {subtitle && (
                <p
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#111729",
                    margin: "2px 0 0",
                  }}
                >
                  {subtitle}
                </p>
              )}
            </div>
          )}

          {/* Question */}
          <p
            style={{
              fontSize: "18px",
              color: "#111729",
              textAlign: "center",
              marginBottom: "30px",
              marginTop: "30px",
            }}
          >
            {message}
          </p>

          {/* ── Buttons ── */}
          <div
            style={{ display: "flex", gap: "10px", justifyContent: "center" }}
          >
            {/* No / Cancel */}
            <button
              type="button"
              onClick={() => !isLoading && onCancel?.()}
              disabled={isLoading}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "5px 30px",
                fontSize: "18px",
                fontWeight: 600,
                color: "#111729",
                background: "#fff",
                border: "1px solid #111729",
                borderRadius: BORDER_RADIUS,
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              {/* ✕ icon */}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path
                  d="M1 1l8 8M9 1L1 9"
                  stroke="#3F3D56"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              {cancelLabel}
            </button>

            {/* Yes / Confirm */}
            <button
              type="button"
              onClick={() => !isLoading && onConfirm?.()}
              disabled={isLoading}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "5px 30px",
                fontSize: "18px",
                fontWeight: 600,
                color: "#fff",
                background: "#111729",
                border: "1px solid #111729",
                borderRadius: BORDER_RADIUS,
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.7 : 1,
                minWidth: "72px",
                justifyContent: "center",
              }}
            >
              {isLoading ? (
                <span
                  style={{
                    width: "13px",
                    height: "13px",
                    border: "2px solid #fff",
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "cdSpin 0.6s linear infinite",
                  }}
                />
              ) : (
                /* ✓ icon */
                <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                  <path
                    d="M1 4l3.5 3.5L11 1"
                    stroke="#fff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {!isLoading && confirmLabel}
            </button>
          </div>
        </div>
      </div>

      <style>{`@keyframes cdSpin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default ConfirmDialog;
